import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon,
  Home06Icon as Home06IconStroke,
  Music02Icon as Music02IconStroke,
  Playlist01Icon as Playlist01IconStroke,
  BookHeadphonesIcon as BookHeadphonesIconStroke,
  ChartIcon as ChartIconStroke,
  Calendar03Icon as Calendar03IconStroke,
  CreditCardIcon as CreditCardIconStroke,
  Money01Icon as Money01IconStroke,
  ArrowRight01Icon,
  Notification03Icon,
  CustomerSupportIcon as CustomerSupportIconStroke,
  DashboardSpeed02Icon as DashboardSpeed02IconStroke,
  Logout03Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Home06Icon as Home06IconSolid,
  Music02Icon as Music02IconSolid,
  Playlist01Icon as Playlist01IconSolid,
  BookHeadphonesIcon as BookHeadphonesIconSolid,
  ChartIcon as ChartIconSolid,
  Calendar03Icon as Calendar03IconSolid,
  CreditCardIcon as CreditCardIconSolid,
  Money01Icon as Money01IconSolid,
} from "@hugeicons-pro/core-solid-rounded";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useOptimizedProfileData } from "@/hooks/profile/useOptimizedProfileData";
import { useNotifications } from "@/hooks/useNotifications";
import { useSidebarActiveContext } from "@/contexts/SidebarActiveContext";
import { getAccountMenuConfig } from "./account-menu/accountMenuNavGroups";
import {
  NAV_ITEMS,
  FOOTER_ITEMS,
  type PublicSidebarSection,
} from "./PublicSidebar";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

type HugeIcon = typeof Cancel01Icon;

const SECTION_ICONS: Record<
  PublicSidebarSection,
  { stroke: HugeIcon; solid: HugeIcon }
> = {
  home: { stroke: Home06IconStroke, solid: Home06IconSolid },
  music: { stroke: Music02IconStroke, solid: Music02IconSolid },
  playlists: { stroke: Playlist01IconStroke, solid: Playlist01IconSolid },
  artists: { stroke: BookHeadphonesIconStroke, solid: BookHeadphonesIconSolid },
  charts: { stroke: ChartIconStroke, solid: ChartIconSolid },
  events: { stroke: Calendar03IconStroke, solid: Calendar03IconSolid },
  plans: { stroke: CreditCardIconStroke, solid: CreditCardIconSolid },
  sponsors: { stroke: Money01IconStroke, solid: Money01IconSolid },
  support: { stroke: CustomerSupportIconStroke, solid: CustomerSupportIconStroke },
};

interface PublicMenuTrayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  active: PublicSidebarSection | null;
}

export function PublicMenuTray({ open, onOpenChange, active }: PublicMenuTrayProps) {
  const { user, userRole, signOut } = useAuth();
  const { profile } = useOptimizedProfileData(user?.id);
  const { unreadCount } = useNotifications();
  const { setAccountSheetOpen } = useSidebarActiveContext();
  const navigate = useNavigate();

  const displayName =
    profile?.display_name || profile?.name || user?.email?.split("@")[0] || "User";

  const { dashboardPath } = getAccountMenuConfig(userRole, unreadCount);

  const openAccountSheet = () => {
    onOpenChange(false);
    setAccountSheetOpen(true);
  };

  const handleSignOut = async () => {
    onOpenChange(false);
    try {
      await signOut();
      toast.success("Signed out successfully");
      navigate("/");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Failed to sign out");
    }
  };

  // Support gets its own footer action below — the main tile grid is just
  // the rail's nav items plus Plans/Sponsors (screen 54a's 8-tile grid).
  const tileItems = [...NAV_ITEMS, ...FOOTER_ITEMS.filter((i) => i.section !== "support")];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideCloseButton
        aria-label="Menu"
        overlayClassName="z-[68] bg-[rgba(10,8,30,0.58)]"
        className="z-[68] border-0 bg-[#1E1B4B] p-0 font-archivo ease-drawer data-[state=open]:duration-300 data-[state=closed]:duration-200 rounded-t-[18px] max-h-[85vh] overflow-y-auto md:bottom-[88px] md:mx-auto md:w-[min(660px,calc(100vw-32px))] md:rounded-[18px]"
      >
        <div className="px-4 pt-2.5">
          <span className="mx-auto mb-1.5 block h-1 w-9 rounded-full bg-white/[.22]" />
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-archivo text-[19px] font-extrabold tracking-[-.01em] text-white">
              Menu
            </h3>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close menu"
              className="-mr-2.5 flex h-11 w-11 items-center justify-center text-white"
            >
              <HugeiconsIcon icon={Cancel01Icon} size={20} strokeWidth={2.2} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 px-4">
          {tileItems.map((item) => {
            const isActive = active === item.section;
            const Icon = SECTION_ICONS[item.section].solid;
            return (
              <Link
                key={item.section}
                to={item.to}
                onClick={() => onOpenChange(false)}
                aria-current={isActive ? "page" : undefined}
                className={`flex h-[72px] flex-col items-center justify-center gap-2 rounded-[10px] text-center ${
                  isActive ? "bg-[#5A31D8]" : "bg-[#2A2660]"
                }`}
              >
                <HugeiconsIcon icon={Icon} size={24} color="#FFFFFF" />
                <span
                  className={`font-archivo text-[11px] leading-none text-white ${
                    isActive ? "font-bold" : "font-semibold"
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>

        <div className="my-3.5 h-px bg-white/[.12]" />

        <div className="px-4 pb-4">
          {user ? (
            <>
              <button
                type="button"
                onClick={openAccountSheet}
                className="flex w-full items-center gap-3 py-1 pb-3 text-left"
              >
                <img
                  src={profile?.avatar_url || "/placeholder.svg"}
                  alt={displayName}
                  className="h-10 w-10 shrink-0 rounded-full bg-[#2A2660] object-cover ring-2 ring-white/90"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-archivo text-[15px] font-bold text-white">
                    {displayName}
                  </span>
                  <span className="block font-archivo text-xs capitalize text-[#C9C5E8]">
                    {userRole || "Guest"} account
                  </span>
                </span>
                <HugeiconsIcon icon={ArrowRight01Icon} size={18} color="rgba(255,255,255,.62)" />
              </button>

              <div className="grid grid-cols-2 gap-2">
                <Link
                  to={dashboardPath}
                  onClick={() => onOpenChange(false)}
                  className="flex h-12 items-center gap-2.5 rounded-[10px] bg-[#2A2660] px-3"
                >
                  <HugeiconsIcon icon={DashboardSpeed02IconStroke} size={20} color="#FFFFFF" />
                  <span className="font-archivo text-[13px] font-semibold text-white">
                    Dashboard
                  </span>
                </Link>
                <Link
                  to={`${dashboardPath}?tab=notifications`}
                  onClick={() => onOpenChange(false)}
                  className="flex h-12 items-center gap-2.5 rounded-[10px] bg-[#2A2660] px-3"
                >
                  <HugeiconsIcon icon={Notification03Icon} size={20} color="#FFFFFF" />
                  <span className="font-archivo text-[13px] font-semibold text-white">
                    Notifications
                  </span>
                  {unreadCount > 0 && (
                    <span className="ml-auto min-w-[18px] rounded-full bg-[#E9B949] px-[5px] py-[1px] text-center font-jetbrains text-[10px] font-bold leading-[18px] text-[#1E1B4B]">
                      {unreadCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/support"
                  onClick={() => onOpenChange(false)}
                  className="flex h-12 items-center gap-2.5 rounded-[10px] bg-[#2A2660] px-3"
                >
                  <HugeiconsIcon icon={CustomerSupportIconStroke} size={20} color="#FFFFFF" />
                  <span className="font-archivo text-[13px] font-semibold text-white">
                    Support
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex h-12 items-center gap-2.5 rounded-[10px] bg-[rgba(220,38,38,.16)] px-3 text-left"
                >
                  <HugeiconsIcon icon={Logout03Icon} size={20} strokeWidth={2.2} color="#FCA5A5" />
                  <span className="font-archivo text-[13px] font-semibold text-[#FCA5A5]">
                    Sign Out
                  </span>
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-2.5">
              <Link
                to="/register"
                onClick={() => onOpenChange(false)}
                className="flex items-center justify-center rounded-[2px] bg-white py-3.5 font-archivo text-sm font-bold text-[#1E1B4B]"
              >
                Start listening
              </Link>
              <Link
                to="/login"
                onClick={() => onOpenChange(false)}
                className="flex items-center justify-center py-1.5 font-archivo text-sm text-[#C9C5E8]"
              >
                Log in
              </Link>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
